import React, { ReactNode } from 'react'
import styled from 'styled-components'
import { themeColors, colors } from '../../const/colors'
import Input from '../../components/Shared/Input'
import { useTranslation } from 'react-i18next'
import { IAccessCardFormat } from '../../graphql/users/queryAccessCardFormat'
import { TextBody1Bold } from '../Styles/TextCustom'
import {
  ACCESS_CARD_NUMBER_REGEX,
  ACCESS_CARD_NUMBER_WITH_COMMA_REGEX,
} from '../../const/regex'
import { splitCardExample, fillCardMask } from '../../utils/accessCardExample'
interface IAccessCardProps {
  cardFormat?: IAccessCardFormat
  formatLabel?: ReactNode
  control: any
  errors: any
  defaultValue?: string
  disabled?: boolean
}

const AccessCardInput = (props: IAccessCardProps) => {
  const { t } = useTranslation(['form'])
  const { cardFormat, formatLabel, control, errors, defaultValue, disabled } =
    props
  const {
    exampleValue = '',
    maxLength,
    minLength,
    name = '',
  } = cardFormat ?? {}
  const {
    prefix: prefixLabel,
    mask,
    suffix: suffixLabel,
  } = splitCardExample(exampleValue)
  const placeholder = fillCardMask(mask)

  const withComma = ['D', 'C', 'E']

  return (
    <CardContainer>
      {formatLabel || <Label>{name}</Label>}
      <Wrapper>
        <Container>
          <CardNumberContainer>
            {prefixLabel && (
              <LabelBold color={themeColors.secondary}>{prefixLabel}</LabelBold>
            )}

            <InputContainer width={name === 'B' ? 190 : 155}>
              <InputStyled
                name="cardNumber"
                placeholder={placeholder.replace(':', ',')}
                label=""
                type="number"
                control={control}
                errors={errors}
                defaultValue={defaultValue?.replace(',', '.') || ''}
                size="small"
                InputProps={{
                  sx: {
                    fontWeight: 700,
                    fontVariantNumeric: 'tabular-nums',
                    color: colors.navy,
                    '& input::placeholder': {
                      fontWeight: 400,
                      color: themeColors.blackHighEmphasis,
                      opacity: 0.42,
                    },
                  },
                }}
                rules={{
                  required: String(t('form:field_required')),
                  maxLength: maxLength
                    ? String(
                        t('valid_card_number_length', {
                          min: minLength,
                          max: maxLength,
                        }),
                      )
                    : undefined,
                  minLength: minLength
                    ? String(
                        t('valid_card_number_length', {
                          min: minLength,
                          max: maxLength,
                        }),
                      )
                    : undefined,
                  pattern: {
                    value: withComma.includes(name)
                      ? ACCESS_CARD_NUMBER_WITH_COMMA_REGEX
                      : ACCESS_CARD_NUMBER_REGEX,
                    message: withComma.includes(name)
                      ? t('valid_card_number_with_comma')
                      : t('valid_card_number'),
                  },
                }}
                disabled={disabled}
                customErrorMessage
                inputProps={{ 'data-e2e': 'access-card-number' }}
              />
            </InputContainer>
            {suffixLabel && (
              <LabelBold color={themeColors.secondary}>{suffixLabel}</LabelBold>
            )}
          </CardNumberContainer>
          {errors?.cardNumber?.message && (
            <InputMessage
              color={themeColors.error}
              data-e2e="access-card-error"
            >
              {errors?.cardNumber?.message}
            </InputMessage>
          )}
        </Container>
      </Wrapper>
    </CardContainer>
  )
}

export default AccessCardInput

const CardContainer = styled.div`
  display: flex;
  flex-direction: column;
  border-radius: 16px;
  width: 360px;
  max-width: 100%;
  aspect-ratio: 1.586 / 1;
  padding: 20px;
  justify-content: space-between;
  background-color: ${colors.bg};
  border: 1.5px solid ${colors.border3};
`
const CardNumberContainer = styled.div`
  display: flex;
  flex-direction: row;
  align-items: flex-end;
  justify-content: flex-end;
`
const InputStyled = styled(Input)`
  margin: 0 8px;
  overflow: hidden;
`
const InputContainer = styled.div<{ width: number }>`
  ${({ width }) => `width: ${width}px;`};
  padding: 0 8px;
`
const LabelBold = styled.p<{ color?: string }>`
  ${({ color }) => color && `color: ${color};`};
  padding-bottom: 10px;
  font-size: 15px;
  font-weight: 600;
`
const Label = styled(TextBody1Bold)`
  color: ${colors.navy};
  font-size: 22px;
  margin-left: 2px;
`
const InputMessage = styled.p<{ color: string }>`
  color: ${props => props.color};
  text-align: right;
  font-size: 12px;
  margin: 0;
`
const Wrapper = styled.div`
  display: flex;
  flex-direction: column;
  flex: 1;
  align-items: flex-end;
`
const Container = styled.div`
  display: flex;
  flex: 1;
  flex-direction: column;
  justify-content: flex-end;
  align-items: flex-end;
`
